import { useState } from 'react';

import { PdfScope } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';

import ArrowDownSmallIcon from '@/components/icons/arrow-down-small';
import DownloadPdfIcon from '@/components/icons/download-pdf';
import QuestionIcon from '@/components/icons/question';
import { TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type DownloadPdfMenuProps = {
  // The singular record noun for a form register (e.g. "inspection"). Omitted for
  // a data register, which offers "Register only" alone.
  recordNoun?: string;
  // Register-specific scopes appended after the standard set (e.g. the issues
  // register's "and repair form records"). Kept generic so this shared menu holds
  // no per-register knowledge. These compose scopes hit the admin-only
  // /exports/pdf-register endpoint, so they are dropped when canExportRegister is
  // false, alongside the built-in register-summary scopes.
  extraItems?: ScopeItem[];
  onSelect: (scope: PdfScope) => void;
  // 'toolbar' collapses the label to an icon below the 3xl breakpoint (matches
  // sibling TablePageHeaderActionButtons); 'inline' keeps the label always
  // visible to match the plain Export CSV / Remove buttons on the users pages.
  variant?: 'toolbar' | 'inline';
  // The register-summary scopes ('register', 'register-and-records') and every
  // extraItems compose scope hit the admin-only /exports/pdf-register endpoint.
  // Pass `false` (e.g. for a Member) to drop them, leaving only the
  // Member-accessible "records only" scope.
  canExportRegister?: boolean;
};

export type ScopeItem = { scope: PdfScope; label: string; help: string };

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildScopeItems(recordNoun: string | undefined, canExportRegister: boolean): ScopeItem[] {
  const registerItem: ScopeItem = {
    scope: 'register',
    label: 'Register only',
    help: 'Download your selected rows as a PDF register summary sheet.',
  };
  if (!recordNoun) return canExportRegister ? [registerItem] : [];
  const recordsItem: ScopeItem = {
    scope: 'records',
    label: `${capitalise(recordNoun)} records only`,
    help: `Download each selected ${recordNoun} record, merged into a single PDF.`,
  };
  if (!canExportRegister) return [recordsItem];
  return [
    registerItem,
    recordsItem,
    {
      scope: 'register-and-records',
      label: `Register and ${recordNoun} records`,
      help: `Download the summary sheet followed by each selected ${recordNoun} record, bound into one PDF.`,
    },
  ];
}

export function DownloadPdfMenu({ recordNoun, extraItems, onSelect, variant = 'toolbar', canExportRegister = true }: DownloadPdfMenuProps) {
  const [open, setOpen] = useState(false);
  const items = [...buildScopeItems(recordNoun, canExportRegister), ...(canExportRegister ? (extraItems ?? []) : [])];
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {variant === 'inline' ? (
          <Button variant="ghost" icon={<DownloadPdfIcon />} className="bg-grey-1 hover:bg-grey-3 px-2">
            Download PDF
            <ArrowDownSmallIcon className="ml-1 size-4 shrink-0" />
          </Button>
        ) : (
          <TablePageHeaderActionButton icon={<DownloadPdfIcon />} label="Download PDF" trailingIcon={<ArrowDownSmallIcon className="size-4 shrink-0" />} />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <TooltipProvider delayDuration={300}>
          {items.map((item) => (
            <DropdownMenuItem
              key={item.scope}
              className="flex items-center justify-between gap-3"
              onClick={() => {
                onSelect(item.scope);
                setOpen(false);
              }}>
              <span>{item.label}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`About ${item.label}`}
                    className="text-grey-5 hover:text-default-text shrink-0"
                    onClick={(event) => event.stopPropagation()}>
                    <QuestionIcon size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent side="right" className="bg-default-text pointer-events-none max-w-64 border-none text-xs text-white">
                    <TooltipArrow className="fill-default-text" />
                    {item.help}
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </DropdownMenuItem>
          ))}
        </TooltipProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
